/**
 * Location Validator
 * UX helper — real security is server-side in Liquid (theme.liquid HMAC check).
 * This script handles: location badge, localStorage→cart resync, kiosk activation redirect.
 */

(function() {
  'use strict';
  // TODO(JK-26): Turn DEBUG off (or remove debug logs) after real-store rollout verification.
  const DEBUG = true;

  class LocationValidator {
    constructor() {
      this.log('Initialized');

      // Handle kiosk activation redirect (admin just logged out)
      if (sessionStorage.getItem('kiosk_activated') === 'true') {
        this.log('Kiosk activated, redirecting to homepage');
        sessionStorage.removeItem('kiosk_activated');
        if (window.location.pathname.includes('/account')) {
          window.location.href = '/';
          return;
        }
      }

      // Skip on management page and account pages
      const path = window.location.pathname;
      if (path.includes('location-management') || path.includes('/account')) {
        this.log('Exempt page, skipping validation');
        return;
      }

      this.validateOnPageLoad();
    }

    log(...args) {
      if (DEBUG) console.log('[LocationValidator]', ...args);
    }

    showRecoveryFailure(message) {
      // JSAU-993: the holding screen now renders on every path (not just /cart), so
      // reveal the hidden failure card and hide the loading spinner. No early return —
      // recovery can fail on the homepage too (e.g. returning after a Pay-at-Counter
      // order with no localStorage token). If there's no holding screen on the page,
      // these elements are absent and this is a no-op.
      const loadingEl = document.getElementById('kiosk-recovery-loading');
      const failureEl = document.getElementById('kiosk-recovery-failure');
      const textEl = document.getElementById('kiosk-recovery-text');

      if (loadingEl) loadingEl.hidden = true;
      if (failureEl) failureEl.hidden = false;
      if (textEl && message) textEl.textContent = message;
    }

    /**
     * Main page-load validation logic
     */
    async validateOnPageLoad() {
      // Read server-side validation result set by Liquid in theme.liquid
      const serverValid = window.__locationValid;
      this.log('Page load check:', window.location.pathname, '| serverValid:', serverValid);

      if (serverValid === true) {
        this.log('Liquid validation passed');
        sessionStorage.removeItem('location_resync_count');
        return;
      }

      // Server says invalid (or undefined). Check if we can resync from localStorage.
      this.log('Liquid validation missing/failed, checking localStorage for resync');

      const lsLocation = localStorage.getItem('location');
      const lsExpires = localStorage.getItem('location_expires');
      const lsAdmin = localStorage.getItem('location_admin');
      const lsSignature = localStorage.getItem('location_signature');

      this.log('localStorage snapshot:', {
        lsLocation,
        lsExpires,
        lsAdmin,
        lsSignature: lsSignature ? lsSignature.substring(0, 16) + '...' : null
      });

      if (!lsLocation || !lsExpires || !lsSignature || !lsAdmin) {
        this.log('localStorage missing token data; no resync possible');
        this.showRecoveryFailure('Location session data was not found. Please contact an administrator.');
        return;
      }

      // Check expiry client-side before attempting resync
      const now = Math.floor(Date.now() / 1000);
      const expiresAt = parseInt(lsExpires, 10);
      if (expiresAt <= now) {
        this.log('localStorage token expired, clearing');
        this.clearLocalStorage();
        this.showRecoveryFailure('Location session expired. Please contact an administrator.');
        return;
      }

      // Check if cart attributes differ from localStorage (need resync)
      const cartAttrs = window.__locationCartAttrs || {};
      const needsResync = (
        cartAttrs.location !== lsLocation ||
        cartAttrs.location_expires !== lsExpires ||
        cartAttrs.location_admin !== lsAdmin ||
        cartAttrs.location_signature !== lsSignature
      );

      if (!needsResync) {
        this.log('Cart attrs match localStorage but still invalid; token likely invalid');
        this.showRecoveryFailure('Location session is invalid. Please contact an administrator.');
        return;
      }

      // Check resync attempt count to prevent infinite reload loops
      const resyncCount = parseInt(sessionStorage.getItem('location_resync_count') || '0', 10);
      if (resyncCount >= 2) {
        this.log('Resync attempted', resyncCount, 'times; stopping to avoid loops');
        sessionStorage.removeItem('location_resync_count');
        this.showRecoveryFailure('Could not restore location session. Please contact an administrator.');
        return;
      }

      // Attempt resync: write localStorage values to cart attributes, then reload
      this.log('Resyncing localStorage to cart attrs. Attempt', resyncCount + 1);
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              location: lsLocation,
              location_expires: lsExpires,
              location_admin: lsAdmin,
              location_signature: lsSignature
            }
          })
        });

        if (!res.ok) {
          console.error('[LocationValidator] Resync cart write failed:', res.status);
          this.showRecoveryFailure('Could not restore location session. Please contact an administrator.');
          return;
        }

        // Verify the write actually stuck by reading cart back
        const verifyRes = await fetch('/cart.js');
        const verifyCart = await verifyRes.json();
        this.log('Cart after resync write:', verifyCart.attributes);

        if (verifyCart.attributes.location_signature === lsSignature) {
          this.log('Resync verified');
          sessionStorage.setItem('location_resync_count', String(resyncCount + 1));
          if (window.location.pathname.startsWith('/cart')) {
            this.log('Recovered from cart state, redirecting to homepage with cart drawer');
            window.location.href = '/?cart=open';
          } else {
            this.log('Reloading current page after resync');
            window.location.reload();
          }
        } else {
          console.error('[LocationValidator] Resync write did not persist — cart attrs:', verifyCart.attributes);
          this.showRecoveryFailure('Could not restore location session. Please contact an administrator.');
        }
      } catch (error) {
        console.error('[LocationValidator] Resync error:', error);
        this.showRecoveryFailure('Could not restore location session. Please contact an administrator.');
      }
    }

    /**
     * Clear localStorage keys
     */
    clearLocalStorage() {
      localStorage.removeItem('location');
      localStorage.removeItem('location_expires');
      localStorage.removeItem('location_admin');
      localStorage.removeItem('location_signature');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.locationValidator = new LocationValidator();
    });
  } else {
    window.locationValidator = new LocationValidator();
  }

  // TODO(JK-26): Remove this debug helper after rollout validation is complete.
  // Debug helper — run window.locationDebug() in console anytime
  window.locationDebug = async function() {
    console.group('[LocationDebug] Full State Dump');

    console.log('--- localStorage ---');
    console.log('  location:', localStorage.getItem('location'));
    console.log('  location_expires:', localStorage.getItem('location_expires'));
    console.log('  location_admin:', localStorage.getItem('location_admin'));
    console.log('  location_signature:', localStorage.getItem('location_signature'));

    console.log('--- Liquid validation (from page render) ---');
    console.log('  __locationValid:', window.__locationValid);
    console.log('  __locationCartAttrs:', window.__locationCartAttrs);
    console.log('  __locationDebug:', window.__locationDebug);

    console.log('--- Live cart (fetching /cart.js) ---');
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      console.log('  attributes:', cart.attributes);
    } catch (e) {
      console.error('  fetch error:', e);
    }

    console.log('--- sessionStorage flags ---');
    console.log('  kiosk_activated:', sessionStorage.getItem('kiosk_activated'));
    console.log('  location_resync_count:', sessionStorage.getItem('location_resync_count'));
    console.log('  location_error:', sessionStorage.getItem('location_error'));

    console.groupEnd();
  };

  if (DEBUG) console.log('[LocationValidator] Script loaded. Debug: window.locationDebug()');
})();
